import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createInitialStatePart, setReducerState } from '../utils/reduxUtils';
import { RootState } from '../store';
import { FailedResponse } from '../models/common';
import { IWebhookInitial, ISecret, WebhookState, ICertificates } from '../models/webhook';
import {
  ILoadWebhookDetailsAction,
  IGenerateNewSecretAction,
  IDeleteWebhookAction,
  IPauseResumeWebhookAction,
  IUpdateWebhookAction,
  IUploadCertificate,
  IDeleteWebhookCertificate
} from '../actionTypes/webhook';

const STATE_KEY = 'webhook';
const initialState = {
  ...createInitialStatePart('webhookDetails'),
  secrets: [],
};

const webhook = createSlice({
  name: STATE_KEY,
  initialState: initialState as IWebhookInitial,
  reducers: {
    loadWebhookDetails: (state, action: PayloadAction<ILoadWebhookDetailsAction>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    loadWebhookDetailsSuccess: (state, action: PayloadAction<WebhookState>) => {
      const newSecrets = action.payload.secrets || [];
      const secrets = newSecrets.filter((c: any) => !c.removalAt);
      return setReducerState('loaded', 'webhookDetails', {
        state: {...state, secrets},
        data: action.payload
      });
    },
    loadWebhookDetailsFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
    generateNewSecret: (state, action: PayloadAction<IGenerateNewSecretAction>) => {
      return setReducerState('loading', 'webhookDetails', { state });
    },
    generateNewSecretSuccess: (state, action: PayloadAction<ISecret[]>) => {
      const secrets = action.payload;
      return {
        ...state,
        webhookDetails: { ...state.webhookDetails, secrets },
        secrets
      }
    },
    generateNewSecretFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
    deleteWebhook: (state, action: PayloadAction<IDeleteWebhookAction>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    deleteWebhookSuccess: (state) => {
      return {
        ...state,
        ...createInitialStatePart('webhookDetails'),
        secrets: []
      };
    },
    deleteWebhookFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, action });
    },
    pauseResumeWebhook: (state, action: PayloadAction<IPauseResumeWebhookAction>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    pauseResumeWebhookSuccess: (state, { payload }: PayloadAction<WebhookState>) => {
      const status = payload.status;
      const data = { ...state.webhookDetails, status };
      return setReducerState('loaded', 'webhookDetails', { state, data });
    },
    pauseResumeWebhookFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
    updateWebhook: (state, action: PayloadAction<IUpdateWebhookAction>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    updateWebhookSuccess: (state, { payload }: PayloadAction<WebhookState>) => {
      const url = payload.url;
      const data = { ...state.webhookDetails, url };
      return setReducerState('loaded', 'webhookDetails', { state, data });
    },
    updateWebhookFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
    uploadWebhookCertificate: (state, action: PayloadAction<IUploadCertificate>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    uploadWebhookCertificateSuccess: (state, { payload }: PayloadAction<ICertificates>) => {
      const certificates = [ ...state.webhookDetails.certificates, payload];
      const data = { ...state.webhookDetails, certificates };
      return setReducerState('loaded', 'webhookDetails', { state, data });
    },
    uploadWebhookCertificateFail: (state, action: PayloadAction<FailedResponse>) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
    deleteWebhookCertificate: (state, action: PayloadAction<IDeleteWebhookCertificate>) => {
      return setReducerState('loading', 'webhookDetails', { state, action });
    },
    deleteWebhookCertificateSuccess: (state, { payload }) => {
      const id = payload.id;
      const certificates = state.webhookDetails.certificates.filter(c => c.id !== id);
      const data = { ...state.webhookDetails, certificates };
      return setReducerState('loaded', 'webhookDetails', { state, data });
    },
    deleteWebhookCertificateFail: (state, action) => {
      return setReducerState('error', 'webhookDetails', { state, error: action.payload });
    },
  },
});

//Actions
export const webhookActions = webhook.actions;

//Selectors
export const secrets = (state: RootState) => state.webhook.secrets;
export const webhookState = (state: RootState) => state.webhook.webhookDetails;
export const webhookStateLoaded = (state: RootState) => state.webhook.webhookDetailsLoaded;

//Reducers
export default webhook.reducer;
